import React, { useEffect, useState } from 'react';
import { DictionaryType, DictionaryMetadata } from '../../types';
import { getAllMetadata, clearDictionary } from '../../services/db';
import { exportDictionaryToFile } from '../../services/export';
import { DictionaryCard } from './DictionaryCard';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { ImportModal } from './ImportModal';
import { useToast } from '../../context/ToastContext';

interface DashboardProps {
  onEdit: (type: DictionaryType) => void;
}

const DICTIONARY_TYPES: DictionaryType[] = [
  'VietPhrase',
  'Name',
  'PhienAm',
  'Pronouns',
  'Rule',
];

export const Dashboard: React.FC<DashboardProps> = ({ onEdit }) => {
  const toast = useToast();
  const [metadataList, setMetadataList] = useState<Record<string, DictionaryMetadata>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteTarget, setDeleteTarget] = useState<DictionaryType | null>(null);
  const [importTarget, setImportTarget] = useState<DictionaryType | null>(null);

  const loadAllMetadata = async () => {
    try {
      setLoading(true);
      const list = await getAllMetadata();
      const map: Record<string, DictionaryMetadata> = {};
      list.forEach((item) => {
        map[item.type] = item;
      });
      setMetadataList(map);
    } catch (err) {
      console.error('Lỗi khi tải metadata từ IndexedDB:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllMetadata();
  }, []);

  const handleDeleteConfirm = async (type: DictionaryType) => {
    try {
      await clearDictionary(type);
      await loadAllMetadata();
      toast.success(`Đã xóa sạch từ điển ${type}!`);
    } catch (err: any) {
      toast.error(`Xóa thất bại: ${err.message || 'Lỗi không xác định'}`);
    }
  };

  const handleExport = async (type: DictionaryType) => {
    try {
      await exportDictionaryToFile(type);
      toast.success(`Đã xuất từ điển ${type} thành file .txt thành công!`);
    } catch (err: any) {
      toast.error(`Xuất file thất bại: ${err.message || 'Lỗi không xác định'}`);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.25rem' }}>
          Từ Điển Cá Nhân
        </h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          Quản lý và chỉnh sửa bộ từ điển Quick Translate
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
          Đang tải dữ liệu từ điển...
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem',
          }}
        >
          {DICTIONARY_TYPES.map((type) => (
            <DictionaryCard
              key={type}
              type={type}
              metadata={metadataList[type] || null}
              onEdit={onEdit}
              onImport={(t) => setImportTarget(t)}
              onExport={handleExport}
              onDelete={(t) => setDeleteTarget(t)}
            />
          ))}
        </div>
      )}

      {/* Confirmation Modal for Delete */}
      <DeleteConfirmModal
        type={deleteTarget}
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Import & Merge Modal */}
      {importTarget && (
        <ImportModal
          dictType={importTarget}
          existingMetadata={metadataList[importTarget] || null}
          isOpen={!!importTarget}
          onClose={() => setImportTarget(null)}
          onSuccess={() => {
            loadAllMetadata();
            toast.success(`Đã nhập từ điển ${importTarget} thành công!`);
          }}
        />
      )}
    </div>
  );
};
